import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  makeStyles,
} from '@material-ui/core';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import { ENVS, Env, isDownstream } from './promoteUtils';

const useStyles = makeStyles((theme) => ({
  section: {
    marginBottom: theme.spacing(2),
  },
  label: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  envGrid: {
    display: 'grid',
    gridTemplateColumns: `repeat(${ENVS.length}, 1fr)`,
    gap: theme.spacing(1),
  },
  envOption: {
    padding: theme.spacing(1.5, 2),
    border: `1.5px solid ${theme.palette.divider}`,
    borderRadius: 6,
    cursor: 'pointer',
    textAlign: 'left',
    background: theme.palette.background.default,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    fontFamily: 'inherit',
    gap: 2,
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
  },
  envOptionActive: {
    borderColor: theme.palette.primary.main,
    background:
      theme.palette.type === 'dark'
        ? 'rgba(139, 92, 246, 0.12)'
        : 'rgba(139, 92, 246, 0.08)',
  },
  envName: {
    fontFamily: 'monospace',
    fontSize: 14,
    fontWeight: 600,
  },
  envSub: {
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
  arrowRow: {
    display: 'flex',
    justifyContent: 'center',
    margin: theme.spacing(1.5, 0),
    color: theme.palette.text.secondary,
  },
  warning: {
    fontSize: 12,
    color: theme.palette.warning.main,
    background:
      theme.palette.type === 'dark'
        ? 'rgba(255, 167, 38, 0.1)'
        : 'rgba(255, 167, 38, 0.08)',
    border: `1px solid ${theme.palette.warning.main}44`,
    borderRadius: 4,
    padding: theme.spacing(1, 1.5),
  },
}));

const ENV_SUB: Record<Env, string> = {
  dev: 'Continuous integration',
  stage: 'Pre-production',
  prod: 'Production',
};

interface PromoteModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (source: Env, target: Env) => void;
}

export const PromoteModal = ({ open, onClose, onConfirm }: PromoteModalProps) => {
  const classes = useStyles();
  const [source, setSource] = useState<Env>('stage');
  const [target, setTarget] = useState<Env>('prod');

  const invalid = !isDownstream(source, target);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Promote set of services</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Pick a source and target. We'll compute the diff and walk you through the promotion.
        </Typography>

        <Box className={classes.section} mt={2}>
          <div className={classes.label}>Source</div>
          <EnvRow
            classes={classes}
            value={source}
            onChange={setSource}
          />
        </Box>

        <div className={classes.arrowRow}>
          <ArrowDownwardIcon fontSize="small" />
        </div>

        <Box className={classes.section}>
          <div className={classes.label}>Target</div>
          <EnvRow
            classes={classes}
            value={target}
            onChange={setTarget}
          />
        </Box>

        {invalid && (
          <div className={classes.warning}>Target must be downstream of source.</div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          disabled={invalid}
          onClick={() => onConfirm(source, target)}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function EnvRow({
  classes,
  value,
  onChange,
}: {
  classes: ReturnType<typeof useStyles>;
  value: Env;
  onChange: (env: Env) => void;
}) {
  return (
    <div className={classes.envGrid}>
      {ENVS.map((env) => {
        const active = value === env;
        return (
          <button
            key={env}
            type="button"
            onClick={() => onChange(env)}
            className={`${classes.envOption} ${active ? classes.envOptionActive : ''}`}
          >
            <span className={classes.envName}>{env}</span>
            <span className={classes.envSub}>{ENV_SUB[env]}</span>
          </button>
        );
      })}
    </div>
  );
}
